import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Copy, Eye, EyeOff, Lock, LockOpen, Save, Timer, Unlock } from 'lucide-react';
import { invoke } from '@tauri-apps/api/core';
import { isTauri } from '../utils/tauri';
import { useChatStore } from '../store/chatStore';
import { openVaultGate, vaultErrorToken } from '../store/vaultStore';
import { ensureE2eKeypair, decryptShare } from '../utils/vaultShareCrypto';
import { copyTextToClipboard } from '../utils/clipboard';
import { copySecretText } from '../utils/clipboardSecret';
import VaultShareSaveModal from './VaultShareSaveModal';
import './Chat.css';

const MASK = '••••••••••••';

const TYPE_FALLBACKS = {
  password: 'Password',
  sftp: 'SFTP connection',
  authHeader: 'Auth header',
  note: 'Note',
  custom: 'Custom item',
};

function typeLabel(t, itemType) {
  const key = {
    password: 'vault.typePassword',
    sftp: 'vault.typeSftp',
    authHeader: 'vault.typeAuthHeader',
    note: 'vault.typeNote',
    custom: 'vault.typeCustom',
  }[itemType];
  if (!key) return itemType || 'Secret';
  return t(key, TYPE_FALLBACKS[itemType]);
}

/** "6d 4h" / "3h 12m" / "45m" — coarse on purpose, ticked every 30s. */
function remainingText(ms) {
  const mins = Math.floor(ms / 60000);
  if (mins < 1) return '<1m';
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ${mins % 60}m`;
  return `${Math.floor(hours / 24)}d ${hours % 24}h`;
}

/** Decrypted-payload rows, per item type (compact FieldRow re-implementation). */
function buildRows(t, type, data) {
  const d = data || {};
  const rows = [];
  const push = (id, label, value, opts = {}) => {
    if (value == null || value === '') return;
    rows.push({ id, label, value: String(value), ...opts });
  };
  switch (type) {
    case 'password':
      push('url', t('vaultShare.fieldUrl', 'URL'), d.url);
      push('username', t('vaultShare.fieldUsername', 'Username'), d.username);
      push('password', t('vaultShare.fieldPassword', 'Password'), d.password, { secret: true });
      push('totp', t('vaultShare.fieldTotp', 'TOTP secret'), d.totp, { secret: true });
      push('notes', t('vaultShare.fieldNotes', 'Notes'), d.notes, { multiline: true });
      break;
    case 'sftp':
      push('protocol', t('vaultShare.fieldProtocol', 'Protocol'), (d.protocol || '').toUpperCase());
      push('host', t('vaultShare.fieldHost', 'Host'), d.host);
      push('port', t('vaultShare.fieldPort', 'Port'), d.port);
      push('username', t('vaultShare.fieldUsername', 'Username'), d.username);
      push('password', t('vaultShare.fieldPassword', 'Password'), d.password, { secret: true });
      push('privateKey', t('vaultShare.fieldPrivateKey', 'Private key'), d.privateKey, {
        secret: true,
        multiline: true,
      });
      push('passphrase', t('vaultShare.fieldPassphrase', 'Passphrase'), d.passphrase, {
        secret: true,
      });
      push('initialPath', t('vaultShare.fieldInitialPath', 'Initial path'), d.initialPath);
      push('notes', t('vaultShare.fieldNotes', 'Notes'), d.notes, { multiline: true });
      break;
    case 'authHeader':
      push('headerName', t('vaultShare.fieldHeaderName', 'Header name'), d.headerName);
      push('headerValue', t('vaultShare.fieldHeaderValue', 'Header value'), d.headerValue, {
        secret: true,
      });
      push('sourceEnv', t('vaultShare.fieldSourceEnv', 'Environment'), d.sourceEnv);
      push('notes', t('vaultShare.fieldNotes', 'Notes'), d.notes, { multiline: true });
      break;
    case 'note':
      push('body', t('vaultShare.fieldBody', 'Note'), d.body, { multiline: true });
      break;
    case 'custom':
      (Array.isArray(d.fields) ? d.fields : []).forEach((f, i) => {
        push(`field-${i}`, f.k || t('vaultShare.fieldValue', 'Value'), f.v, {
          secret: !!f.secret,
        });
      });
      push('notes', t('vaultShare.fieldNotes', 'Notes'), d.notes, { multiline: true });
      break;
    default:
      break;
  }
  return rows;
}

/** One decrypted row: secrets masked with reveal + copy. */
function SecretFieldRow({ row, t }) {
  const [revealed, setRevealed] = useState(false);
  const [copied, setCopied] = useState(false);

  const doCopy = async () => {
    // Secret rows (passwords, keys, header values) route through the shared
    // secret-copy helper so they inherit the 60s clipboard auto-clear, same as
    // the vault detail pane — non-secret rows copy plainly.
    const ok = row.secret
      ? await copySecretText(row.value)
      : await copyTextToClipboard(row.value);
    if (!ok) return;
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const shown = row.secret && !revealed ? MASK : row.value;
  return (
    <div className="chat-secret-field">
      <span className="chat-secret-field-label">{row.label}</span>
      <div className="chat-secret-field-value">
        <span
          className={`chat-secret-field-text${
            row.multiline && (!row.secret || revealed) ? ' chat-secret-field-text--multiline' : ''
          }`}
        >
          {shown}
        </span>
        {row.secret && (
          <button
            type="button"
            className="chat-secret-field-btn"
            title={revealed ? t('vaultShare.hide', 'Hide') : t('vaultShare.reveal', 'Reveal')}
            onClick={() => setRevealed((r) => !r)}
          >
            {revealed ? <EyeOff size={12} /> : <Eye size={12} />}
          </button>
        )}
        <button
          type="button"
          className={`chat-secret-field-btn${copied ? ' chat-secret-field-btn--ok' : ''}`}
          title={copied ? t('vaultShare.copied', 'Copied') : t('vaultShare.copy', 'Copy')}
          onClick={doCopy}
        >
          {copied ? <Check size={12} /> : <Copy size={12} />}
        </button>
      </div>
    </div>
  );
}

/**
 * Chat card for `kind:"secret"` messages: an E2E-encrypted vault item. Sealed
 * until the recipient decrypts in place (vault must be unlocked — the keypair
 * lives there); the decrypted payload only ever exists in this component's
 * state, never in chatStore. Expired shares dim locally the moment the
 * deadline passes (the server sweeper scrubs them for real shortly after).
 */
export default function VaultShareCard({
  metadata,
  senderName,
  senderUserId,
  channelId,
  messageId,
}) {
  const { t } = useTranslation();
  const meta = metadata || {};
  const myUserId = useChatStore((s) => s.currentUserId);

  // 'sealed' | 'locked' | 'revealed' | 'no_key' | 'unsupported' | 'decrypt_failed'
  const [phase, setPhase] = useState('sealed');
  const [busy, setBusy] = useState(false);
  const [errorText, setErrorText] = useState('');
  // Decrypted { type, name, data } — component state ONLY (dropped on unmount).
  const [payload, setPayload] = useState(null);
  const [saveOpen, setSaveOpen] = useState(false);
  // Re-render tick for the countdown chip / local expiry flip.
  const [now, setNow] = useState(() => Date.now());

  const expiresAtMs = meta.expires_at ? new Date(meta.expires_at).getTime() : null;
  const expired =
    Boolean(meta.expired) || (expiresAtMs != null && !Number.isNaN(expiresAtMs) && expiresAtMs <= now);

  useEffect(() => {
    if (!expiresAtMs || expired) return undefined;
    const id = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(id);
  }, [expiresAtMs, expired]);

  // The moment a share expires, drop any revealed plaintext too.
  useEffect(() => {
    if (expired) {
      setPayload(null);
      setSaveOpen(false);
    }
  }, [expired]);

  const doDecrypt = async () => {
    if (busy) return;
    setBusy(true);
    setErrorText('');
    try {
      const status = await invoke('vault_status');
      if (!status?.unlocked) {
        setPhase('locked');
        return;
      }
      const { privateKey } = await ensureE2eKeypair();
      // v2 AAD context. `channelId` is the channel this card is being RENDERED
      // in and `senderUserId` is who the client attributes the message to — a
      // v2 envelope relocated to another channel or re-attributed to another
      // sender fails the GCM tag instead of quietly opening.
      const decrypted = await decryptShare(meta, myUserId, privateKey, {
        channelId,
        senderUserId,
      });
      setPayload(decrypted);
      setPhase('revealed');
    } catch (e) {
      if (e?.code === 'no_key') setPhase('no_key');
      else if (e?.code === 'unsupported_version') setPhase('unsupported');
      else setPhase('decrypt_failed');
    } finally {
      setBusy(false);
    }
  };

  const doUnlock = async () => {
    if (busy) return;
    setBusy(true);
    setErrorText('');
    try {
      await invoke('vault_unlock');
      setPhase('sealed');
      setBusy(false);
      await doDecrypt();
      return;
    } catch (e) {
      // A Protected vault wants the passphrase. Painting "Could not unlock the
      // vault" here would be a dead end: this card is the only way to open a
      // received secret, and it offers no other route to unlock.
      // `vault_needs_bootstrap` (never-bootstrapped scope) and
      // `vault_kdf_corrupt` (unopenable-with-any-secret kdf block) are the
      // same request-for-the-gate shape (vault-storage-transition phase 7).
      if (
        vaultErrorToken(e) === 'passphrase_required' ||
        vaultErrorToken(e) === 'vault_needs_bootstrap' ||
        vaultErrorToken(e) === 'vault_kdf_corrupt'
      ) {
        openVaultGate('share');
        setBusy(false);
        return;
      }
      setErrorText(t('vaultShare.unlockFailed', 'Could not unlock the vault'));
    }
    setBusy(false);
  };

  const label = meta.label || t('vaultShare.encryptedSecret', 'Encrypted secret');
  const isSftp = (payload?.type || meta.item_type) === 'sftp';
  const rows = payload ? buildRows(t, payload.type, payload.data) : [];

  return (
    <div className={`chat-secret-card${expired ? ' chat-secret-card--expired' : ''}`}>
      <div className="chat-secret-head">
        <div className="chat-secret-lock">
          {phase === 'revealed' && !expired ? <LockOpen size={14} /> : <Lock size={14} />}
        </div>
        <div className="chat-secret-titles">
          <span className="chat-secret-label">{label}</span>
          <span className="chat-secret-sub">
            {senderName
              ? t('vaultShare.fromSender', { defaultValue: 'from {{name}}', name: senderName })
              : t('vaultShare.sharedSecret', 'shared secret')}
          </span>
        </div>
        {meta.item_type && (
          <span className="chat-secret-chip">{typeLabel(t, meta.item_type)}</span>
        )}
        {!expired && expiresAtMs != null && !Number.isNaN(expiresAtMs) && (
          <span className="chat-secret-chip chat-secret-chip--timer" title={new Date(expiresAtMs).toLocaleString()}>
            <Timer size={10} />
            {remainingText(expiresAtMs - now)}
          </span>
        )}
      </div>

      {expired ? (
        <div className="chat-secret-note chat-secret-note--dim">
          {t('vaultShare.expired', 'This secret has expired')}
        </div>
      ) : phase === 'no_key' ? (
        <div className="chat-secret-note">
          {t('vaultShare.noKey', "This secret wasn't shared with this device/account")}
        </div>
      ) : phase === 'unsupported' ? (
        <div className="chat-secret-note chat-secret-note--error">
          {t(
            'vaultShare.unsupportedVersion',
            'This secret was encrypted by a newer version of apiboo — update to open it'
          )}
        </div>
      ) : phase === 'decrypt_failed' ? (
        <div className="chat-secret-note chat-secret-note--error">
          {t('vaultShare.decryptFailed', "Couldn't decrypt this secret on this device")}
        </div>
      ) : phase === 'revealed' && payload ? (
        <>
          <div className="chat-secret-fields">
            {rows.map((row) => (
              <SecretFieldRow key={`${messageId || 'msg'}:${row.id}`} row={row} t={t} />
            ))}
          </div>
          {isTauri() && (
            <button
              type="button"
              className={`chat-secret-btn${isSftp ? ' chat-secret-btn--primary' : ''}`}
              onClick={() => setSaveOpen(true)}
            >
              <Save size={13} />
              {isSftp
                ? t('vaultShare.saveSftp', 'Save to my vault')
                : t('vaultShare.save', 'Save to vault')}
            </button>
          )}
        </>
      ) : phase === 'locked' ? (
        <>
          <div className="chat-secret-note">
            {t('vaultShare.vaultLocked', 'Unlock your vault to decrypt this secret')}
          </div>
          <button
            type="button"
            className="chat-secret-btn chat-secret-btn--primary"
            disabled={busy}
            onClick={doUnlock}
          >
            <Unlock size={13} />
            {busy ? t('vaultShare.unlocking', 'Unlocking…') : t('vaultShare.unlockVault', 'Unlock vault')}
          </button>
        </>
      ) : isTauri() ? (
        <button
          type="button"
          className="chat-secret-btn chat-secret-btn--primary"
          disabled={busy}
          onClick={doDecrypt}
        >
          <Unlock size={13} />
          {busy ? t('vaultShare.decrypting', 'Decrypting…') : t('vaultShare.decrypt', 'Decrypt')}
        </button>
      ) : (
        <div className="chat-secret-note">
          {t('vaultShare.desktopOnly', 'Open the desktop app to decrypt this secret')}
        </div>
      )}

      {errorText && <div className="chat-secret-note chat-secret-note--error">{errorText}</div>}

      <VaultShareSaveModal
        isOpen={saveOpen}
        onClose={() => setSaveOpen(false)}
        payload={payload}
        senderName={senderName}
      />
    </div>
  );
}
